import { createSelector } from '@reduxjs/toolkit';
import { MessageRole } from '../types/message.types';
import { SSEEventPayloadType } from '../types/sse.types';
import { conversationsSelectors } from './assistantConversationSlice';
import { assistantMessagesAdapter } from './assistantMessageSlice';
export const selectAssistantStatus = (state) => state.assistant.status;
export const selectConversationBlockingError = (state) => state.assistant.conversationBlockingError;
export const selectMessageStreamingError = (state) => state.assistant.messageStreamingError;
export const selectIsConnected = (state) => state.assistant.isConnected;
export const selectModelAutoSwitch = (state) => state.assistant.modelAutoSwitch;
export const selectAllConversations = (state) => conversationsSelectors.selectAll(state);
const selectScopedCompanyId = (_state, companyId) => companyId || null;
const selectScopedGrantApplicationId = (_state, _companyId, grantApplicationId) => grantApplicationId || null;
const selectScopedEOSAccountabilityChartId = (_state, _companyId, _grantApplicationId, chartId) => chartId || null;
export const selectScopedConversations = createSelector([
    selectAllConversations,
    selectScopedCompanyId,
    selectScopedGrantApplicationId,
    selectScopedEOSAccountabilityChartId,
], (conversations, companyId, grantApplicationId, chartId) => {
    if (!companyId && !grantApplicationId && !chartId) {
        return conversations;
    }
    return conversations.filter((conversation) => {
        var _a, _b, _c;
        if (companyId && ((_a = conversation.usage_context) === null || _a === void 0 ? void 0 : _a.company_id) !== companyId) {
            return false;
        }
        if (grantApplicationId &&
            ((_b = conversation.usage_context) === null || _b === void 0 ? void 0 : _b.grant_application_id) !== grantApplicationId) {
            return false;
        }
        if (chartId &&
            ((_c = conversation.usage_context) === null || _c === void 0 ? void 0 : _c.eos_accountability_chart_id) !== chartId) {
            return false;
        }
        return true;
    });
});
export const selectScopedConversationCount = createSelector([selectScopedConversations], (conversations) => conversations.length);
export const selectActiveConversationId = (state) => state.conversations.activeId;
export const selectActiveConversation = createSelector([conversationsSelectors.selectEntities, selectActiveConversationId], (conversationsById, activeId) => activeId ? conversationsById[activeId] : null);
export const selectMessageById = (state, conversationId, messageId) => assistantMessagesAdapter
    .getSelectors()
    .selectById(state.messages.messages[conversationId], messageId);
export const selectConversationMessages = createSelector([
    (state, conversationId) => state.messages.messages[conversationId],
], (messages) => messages
    ? assistantMessagesAdapter.getSelectors().selectAll(messages)
    : undefined);
export const selectIsStreaming = createSelector([
    (state, conversationId) => selectConversationMessages(state, conversationId),
], (messages) => {
    if (!messages)
        return false;
    return messages.some((m) => {
        var _a;
        if (!((_a = m.meta) === null || _a === void 0 ? void 0 : _a.sse))
            return false;
        const hasComplete = m.meta.sse.some((e) => e.type === 'assistant_complete');
        const hasChunks = m.meta.sse.some((e) => e.type === 'assistant_message_chunk');
        return hasChunks && !hasComplete;
    });
});
export const selectConversationStillProcessing = createSelector([
    (state, conversationId) => selectConversationMessages(state, conversationId),
], (messages) => {
    if (!messages || messages.length === 0)
        return false;
    const lastMessage = messages[messages.length - 1];
    if ((lastMessage === null || lastMessage === void 0 ? void 0 : lastMessage.role) === MessageRole.USER) {
        return true;
    }
    return messages.some((message) => {
        var _a;
        const sseEvents = ((_a = message.meta) === null || _a === void 0 ? void 0 : _a.sse) || [];
        if (sseEvents.length === 0)
            return false;
        const hasError = sseEvents.some((event) => event.type === SSEEventPayloadType.ASSISTANT_ERROR);
        if (message.role === MessageRole.ASSISTANT) {
            const hasChunks = sseEvents.some((event) => event.type === SSEEventPayloadType.ASSISTANT_MESSAGE_CHUNK);
            const hasComplete = sseEvents.some((event) => event.type === SSEEventPayloadType.ASSISTANT_COMPLETE);
            return hasChunks && !hasComplete && !hasError;
        }
        if (message.role === MessageRole.TOOL) {
            const hasToolStart = sseEvents.some((event) => event.type === SSEEventPayloadType.ASSISTANT_TOOL_USE_START);
            const hasToolEnd = sseEvents.some((event) => event.type === SSEEventPayloadType.ASSISTANT_TOOL_USE_END);
            return hasToolStart && !hasToolEnd && !hasError;
        }
        return false;
    });
});
export const selectConversationRequiresWaitNotice = createSelector([
    (state, conversationId) => selectConversationStillProcessing(state, conversationId),
    (state, conversationId) => !!state.assistant.processingNoticeConversationIds[conversationId],
], (isStillProcessing, hasDeferredProcessingNotice) => isStillProcessing && hasDeferredProcessingNotice);
export const selectActiveToolMessage = createSelector([(state) => state.assistant.activeToolCall], (activeToolCall) => { var _a; return (_a = activeToolCall === null || activeToolCall === void 0 ? void 0 : activeToolCall.function) === null || _a === void 0 ? void 0 : _a.name; });
export const selectConversationTitle = createSelector([
    (state, conversationId) => conversationsSelectors.selectById(state, conversationId),
], (conversation) => {
    return (conversation === null || conversation === void 0 ? void 0 : conversation.title) && conversation.title.trim() !== ''
        ? conversation.title
        : null;
});
export const selectConversationLastActivity = createSelector([
    (state, conversationId) => selectConversationMessages(state, conversationId),
    (state, conversationId) => conversationsSelectors.selectById(state, conversationId),
], (messages, conversation) => {
    const lastMessage = messages === null || messages === void 0 ? void 0 : messages.slice().reverse()[0];
    return ((lastMessage === null || lastMessage === void 0 ? void 0 : lastMessage.timestamp) ||
        (conversation === null || conversation === void 0 ? void 0 : conversation.created_at) ||
        new Date().toISOString());
});
export const selectContextCompany = createSelector([selectActiveConversation], (conversation) => {
    var _a, _b;
    if (!((_a = conversation === null || conversation === void 0 ? void 0 : conversation.usage_context) === null || _a === void 0 ? void 0 : _a.company_id)) {
        return { id: null, name: null };
    }
    return {
        id: conversation.usage_context.company_id,
        name: (_b = conversation.company_name) !== null && _b !== void 0 ? _b : null,
    };
});
export const selectContextGrantApplication = createSelector([selectActiveConversation], (conversation) => {
    var _a, _b;
    if (!((_a = conversation === null || conversation === void 0 ? void 0 : conversation.usage_context) === null || _a === void 0 ? void 0 : _a.grant_application_id)) {
        return { id: null, name: null, programId: null };
    }
    const fallbackName = conversation.application_display ||
        conversation.program_id ||
        conversation.usage_context.grant_application_id;
    return {
        id: conversation.usage_context.grant_application_id,
        name: fallbackName,
        programId: (_b = conversation.program_id) !== null && _b !== void 0 ? _b : null,
    };
});
export const selectHasContext = createSelector([selectActiveConversation], (conversation) => {
    var _a, _b;
    return !!((_a = conversation === null || conversation === void 0 ? void 0 : conversation.usage_context) === null || _a === void 0 ? void 0 : _a.company_id) ||
        !!((_b = conversation === null || conversation === void 0 ? void 0 : conversation.usage_context) === null || _b === void 0 ? void 0 : _b.grant_application_id);
});
